import { useQuery, useMutation, queryCache } from 'react-query'
import { UserRepository } from '../repository/user'
import { UserFilters, User, UserParams, ChangePasswordFields } from 'models/user'
import { UserFormFields } from 'features/Organization/components/UserForm/UserForm'
import { UserPermissionsUpdateParams } from 'models/user-permissions'

export function useFetchUsers(filters: UserFilters) {
  const { status, data: users, error } = useQuery(['users', filters], () =>
    UserRepository.fetchAll(filters),
  )

  return {
    status,
    users,
    error,
  }
}

export function useFetchUser(userId: string) {
  const { status, data: user, error } = useQuery(['user', { userId }], () =>
    UserRepository.fetch(userId),
  )
  return { status, user, error }
}

export function useFetchUserPermissions(userId: string, options?: any) {
  const { status, data: permissions, error } = useQuery(
    !!userId && ['user-permissions', { userId }],
    () => UserRepository.fetchPermissions(userId),
  )
  return { status, permissions, error }
}

export function useCreateUser() {
  return useMutation((data: UserFormFields) => UserRepository.create(data))
}

export function useUpdateUser(userId: string) {
  return useMutation((user: Partial<User>) => UserRepository.update(userId, user), {
    onSuccess: (user: UserParams) => {
      queryCache.setQueryData(['user', { userId: `${userId}` }], user)
      queryCache.setQueryData(['users'], (oldUsers: User[] = []) =>
        oldUsers.map((oldUser) => (oldUser.id === user.id ? new User(user) : oldUser)),
      )
    },
  })
}

export function useUpdateCurrentUser() {
  return useMutation((user: Partial<User>) => UserRepository.updateCurrentUser(user), {
    onSuccess: (user: User) => {
      queryCache.setQueryData(['me'], user)
    },
  })
}

export function useRemoveUser(userId: string) {
  return useMutation(() => UserRepository.delete(userId), {
    onSuccess: () => {
      queryCache.removeQueries(['user', { userId }])
      queryCache.removeQueries(['user-permissions', { userId }])
      queryCache.setQueryData(['users'], (users: User[] = []) =>
        users.filter((user) => user.id !== userId),
      )
    },
  })
}

export function useUpdatePassword() {
  return useMutation((data: ChangePasswordFields) => UserRepository.resetPassword(data))
}

export function useUpdatePermissions(userId: string) {
  return useMutation(
    (data: UserPermissionsUpdateParams) => UserRepository.updatePermissions(userId, data),
    {
      onSuccess: (data: User) => {
        queryCache.setQueryData(['user-permissions', { userId: `${userId}` }], data)
      },
    },
  )
}
